import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Check, ChevronDown, Lock, Search, X } from "lucide-react";
import { Checkbox, Popover, PopoverAnchor, PopoverContent, PopoverTrigger, cn } from "@nexus/ui";
import {
  FILTER_TRIGGER_BASE,
  FILTER_TRIGGER_LABEL,
  FILTER_TRIGGER_VALUE,
} from "@/features/dashboard/lib/filter-trigger-styles";
import { OrgLogoMark } from "./OrgLogoMark";
import type { OrgPickerItem } from "./OrgPickerCombobox";

const DEFAULT_MAX_SHOWN = 2;
const LIST_LOGO_SIZE = 20;
const CHIP_LOGO_SIZE = 18;
const TABBABLE_SELECTOR =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

interface OrgFilterComboBase {
  items: OrgPickerItem[];
  label: string;
  ariaLabel: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  /**
   * `filter` is the compact toolbar chip ("Label: A, B +2"). `field` is a full-width form
   * control that shows logos: removable chips for multi, one org row for single.
   */
  variant?: "filter" | "field";
  /** Field variant only: shown under the org when the control is disabled. */
  disabledReason?: string;
  className?: string;
}

export interface OrgFilterComboSingleProps extends OrgFilterComboBase {
  mode: "single";
  value: string;
  onValueChange: (id: string) => void;
}

export interface OrgFilterComboMultiProps extends OrgFilterComboBase {
  mode: "multi";
  value: string[];
  onValueChange: (ids: string[]) => void;
  maxShown?: number;
}

export type OrgFilterComboProps = OrgFilterComboSingleProps | OrgFilterComboMultiProps;

export function OrgFilterCombo(props: OrgFilterComboProps) {
  const { t } = useTranslation();
  const { items, label, ariaLabel, disabled = false, variant = "filter", className } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  /** Selection when the list opened — keeps rows from jumping between groups while toggling. */
  const [openedSelection, setOpenedSelection] = useState<ReadonlySet<string>>(() => new Set());
  const listboxId = useId();
  const [activeIndex, setActiveIndex] = useState(0);
  const isClosingByTabRef = useRef(false);
  const isSingle = props.mode === "single";

  const itemsById = useMemo(() => new Map(items.map((org) => [org.id, org])), [items]);
  const selectedIds = props.mode === "single" ? (props.value ? [props.value] : []) : props.value;
  const selectedOrgs = selectedIds
    .map((id) => itemsById.get(id))
    .filter((org): org is OrgPickerItem => !!org);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((org) => org.name.toLowerCase().includes(q));
  }, [items, query]);

  const optionId = (orgId: string) => `${listboxId}-${orgId}`;
  const activeOrg = isSingle ? filteredItems[activeIndex] : undefined;

  useEffect(() => {
    if (!open || !activeOrg) return;
    document.getElementById(optionId(activeOrg.id))?.scrollIntoView?.({ block: "nearest" });
  });

  const handleOpenChange = (next: boolean) => {
    if (disabled) return;
    setOpen(next);
    if (next) {
      setOpenedSelection(new Set(selectedIds));
      setActiveIndex(Math.max(0, items.findIndex((org) => selectedIds.includes(org.id))));
    } else {
      setQuery("");
    }
  };

  const handleQueryChange = (next: string) => {
    setQuery(next);
    setActiveIndex(0);
  };

  const handleToggle = (orgId: string) => {
    if (props.mode === "single") {
      props.onValueChange(orgId);
      handleOpenChange(false);
      return;
    }
    if (props.value.includes(orgId)) {
      props.onValueChange(props.value.filter((id) => id !== orgId));
      return;
    }
    props.onValueChange([...props.value, orgId]);
  };

  /** ARIA combobox keys for the single-select list; focus stays in the search box throughout. */
  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isSingle) return;
    const lastIndex = filteredItems.length - 1;
    const targets: Record<string, number> = {
      ArrowDown: Math.min(activeIndex + 1, lastIndex),
      ArrowUp: Math.max(activeIndex - 1, 0),
      Home: 0,
      End: lastIndex,
    };
    if (event.key in targets) {
      event.preventDefault();
      if (lastIndex >= 0) setActiveIndex(targets[event.key]);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (activeOrg) handleToggle(activeOrg.id);
      return;
    }
    if (event.key === "Tab") {
      event.preventDefault();
      isClosingByTabRef.current = true;
      handleOpenChange(false);
    }
  };

  const handleCloseAutoFocus = () => {
    if (!isClosingByTabRef.current) return;
    isClosingByTabRef.current = false;
    requestAnimationFrame(() => focusNextTabbable(document.activeElement));
  };

  const allSelected = props.mode === "multi"
    && items.length > 0
    && items.every((org) => props.value.includes(org.id));

  const handleToggleAll = () => {
    if (props.mode !== "multi") return;
    props.onValueChange(allSelected ? [] : items.map((org) => org.id));
  };

  const searchPlaceholder = props.searchPlaceholder
    ?? t("iam.templates.dialog.organizationsSearchPlaceholder", "Search organizations…");
  const maxShown = props.mode === "multi" ? (props.maxShown ?? DEFAULT_MAX_SHOWN) : DEFAULT_MAX_SHOWN;

  const trigger = variant === "field" ? (
    <FieldTrigger
      mode={props.mode}
      selectedOrgs={selectedOrgs}
      maxShown={maxShown}
      ariaLabel={ariaLabel}
      open={open}
      disabled={disabled}
      disabledReason={props.disabledReason}
      className={className}
      t={t}
      onRemove={props.mode === "multi" ? handleToggle : undefined}
    />
  ) : (
    <PopoverTrigger asChild>
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        disabled={disabled}
        className={cn(
          "flex min-w-0 items-center gap-1.5",
          FILTER_TRIGGER_BASE,
          disabled && "cursor-not-allowed border-border bg-muted/40 text-muted-foreground opacity-50 hover:bg-muted/40",
          className,
        )}
      >
        <span className={FILTER_TRIGGER_LABEL}>{label}:</span>
        <FilterTriggerValue selectedOrgs={selectedOrgs} allSelected={allSelected} maxShown={maxShown} t={t} />
        <ChevronDown className="h-3 w-3 shrink-0 opacity-50" aria-hidden />
      </button>
    </PopoverTrigger>
  );

  const renderRow = (org: OrgPickerItem, index: number) => (
    <OrgOptionRow
      key={org.id}
      id={optionId(org.id)}
      mode={props.mode}
      org={org}
      isSelected={selectedIds.includes(org.id)}
      isActive={activeOrg?.id === org.id}
      t={t}
      onToggle={() => handleToggle(org.id)}
      onHover={isSingle ? () => setActiveIndex(index) : undefined}
    />
  );

  const earlierSelected = props.mode === "multi" && !query
    ? filteredItems.filter((org) => openedSelection.has(org.id))
    : [];
  const rest = earlierSelected.length > 0
    ? filteredItems.filter((org) => !openedSelection.has(org.id))
    : filteredItems;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {trigger}
      <PopoverContent align="start" className="w-80 p-0" onCloseAutoFocus={handleCloseAutoFocus}>
        <div className="flex flex-col gap-2 border-b border-border p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              {...(isSingle && {
                role: "combobox",
                "aria-expanded": true,
                "aria-controls": listboxId,
                "aria-autocomplete": "list" as const,
                "aria-activedescendant": activeOrg ? optionId(activeOrg.id) : undefined,
              })}
              className="h-8 w-full rounded-md border border-input bg-transparent pl-8 pr-2 text-xs outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
            />
          </div>
          {props.mode === "multi" && items.length > 0 && (
            <label className="flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1 text-xs font-medium hover:bg-accent">
              <Checkbox
                checked={allSelected}
                onCheckedChange={handleToggleAll}
                aria-label={t("iam.templates.dialog.filterAll", "All")}
                className="h-3.5 w-3.5"
              />
              <span>{t("iam.templates.dialog.filterAll", "All")}</span>
            </label>
          )}
        </div>
        <div
          id={listboxId}
          role={props.mode === "single" ? "listbox" : undefined}
          aria-label={props.mode === "single" ? ariaLabel : undefined}
          className="max-h-64 overflow-y-auto p-1"
          onWheel={(event) => event.stopPropagation()}
        >
          {filteredItems.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              {t("iam.templates.dialog.filterNoOrgs", "No organizations found.")}
            </p>
          ) : earlierSelected.length > 0 ? (
            <>
              <OptionGroupHeading>{t("iam.templates.dialog.pickerSelectedGroup", "Selected")}</OptionGroupHeading>
              {earlierSelected.map(renderRow)}
              {rest.length > 0 && (
                <OptionGroupHeading>{t("iam.templates.dialog.pickerOtherGroup", "Other organizations")}</OptionGroupHeading>
              )}
              {rest.map(renderRow)}
            </>
          ) : (
            rest.map(renderRow)
          )}
        </div>
        {props.mode === "multi" && (
          <p className="border-t border-border bg-muted px-3 py-2 text-[11px] tabular-nums text-muted-foreground" aria-live="polite">
            {t("iam.templates.dialog.pickerSelectedCount", "{{selected}} of {{total}} selected", {
              selected: selectedOrgs.length,
              total: items.length,
            })}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

function FilterTriggerValue({
  selectedOrgs,
  allSelected,
  maxShown,
  t,
}: {
  selectedOrgs: OrgPickerItem[];
  allSelected: boolean;
  maxShown: number;
  t: TFunction;
}) {
  if (selectedOrgs.length === 0) {
    return <span className="shrink-0 text-muted-foreground">{t("iam.templates.dialog.filterNone", "None")}</span>;
  }
  if (allSelected) return <span className={FILTER_TRIGGER_VALUE}>{t("iam.templates.dialog.filterAll", "All")}</span>;
  const extraCount = Math.max(selectedOrgs.length - maxShown, 0);
  return (
    <>
      <span className={cn("min-w-0 truncate", FILTER_TRIGGER_VALUE)}>
        {selectedOrgs.slice(0, maxShown).map((org) => org.name).join(", ")}
      </span>
      {extraCount > 0 && (
        <span className={FILTER_TRIGGER_VALUE}>
          {t("iam.templates.dialog.filterOverflow", "+{{count}}", { count: extraCount })}
        </span>
      )}
    </>
  );
}

function FieldTrigger({
  mode,
  selectedOrgs,
  maxShown,
  ariaLabel,
  open,
  disabled,
  disabledReason,
  className,
  t,
  onRemove,
}: {
  mode: "single" | "multi";
  selectedOrgs: OrgPickerItem[];
  maxShown: number;
  ariaLabel: string;
  open: boolean;
  disabled: boolean;
  disabledReason?: string;
  className?: string;
  t: TFunction;
  onRemove?: (orgId: string) => void;
}) {
  const comboboxProps = {
    type: "button" as const,
    role: "combobox",
    "aria-expanded": open,
    "aria-haspopup": "listbox" as const,
    "aria-label": ariaLabel,
    disabled,
  };

  if (mode === "single") {
    const org = selectedOrgs[0];
    return (
      <PopoverTrigger asChild>
        <button
          {...comboboxProps}
          className={cn(
            "flex min-h-11 w-full items-center gap-3 rounded-md border px-3 py-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
            disabled ? "cursor-not-allowed border-border bg-muted" : "border-input-border bg-background hover:bg-muted/50",
            className,
          )}
        >
          {org ? <OrgMark org={org} size={LIST_LOGO_SIZE + 4} /> : null}
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="flex items-center gap-1.5">
              <span className="truncate text-sm font-medium text-foreground">
                {org?.name ?? t("iam.templates.dialog.filterNone", "None")}
              </span>
              {org && <OrgTypeTag type={org.type} t={t} />}
            </span>
            {disabled && disabledReason && (
              <span className="text-[11px] text-muted-foreground">{disabledReason}</span>
            )}
          </span>
          {disabled
            ? <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />}
        </button>
      </PopoverTrigger>
    );
  }

  const extraCount = Math.max(selectedOrgs.length - maxShown, 0);
  return (
    <PopoverAnchor asChild>
      <div
        className={cn(
          "flex min-h-11 w-full flex-wrap items-center gap-1.5 rounded-md border border-input-border bg-background px-1.5 py-1.5",
          disabled && "cursor-not-allowed bg-muted",
          className,
        )}
      >
        {selectedOrgs.length === 0 && (
          <span className="px-1.5 text-sm text-muted-foreground">
            {t("iam.templates.dialog.pickerEmpty", "No organizations yet")}
          </span>
        )}
        {selectedOrgs.slice(0, maxShown).map((org) => (
          <span key={org.id} className="flex h-7 items-center gap-1.5 rounded-md border border-border bg-muted pl-1 pr-1">
            <OrgMark org={org} size={CHIP_LOGO_SIZE} />
            <span className="max-w-48 truncate text-xs text-foreground">{org.name}</span>
            {onRemove && !disabled && (
              <button
                type="button"
                onClick={() => onRemove(org.id)}
                aria-label={t("iam.templates.dialog.pickerRemoveOrg", "Remove {{name}}", { name: org.name })}
                className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground outline-none hover:bg-background hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            )}
          </span>
        ))}
        {extraCount > 0 && (
          <span className="flex h-7 items-center rounded-md bg-primary-subtle px-2 text-xs font-medium text-primary-subtle-foreground">
            {t("iam.templates.dialog.pickerMore", "+{{count}} more", { count: extraCount })}
          </span>
        )}
        <PopoverTrigger asChild>
          <button
            {...comboboxProps}
            className="ml-auto flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium text-primary outline-none hover:bg-primary-subtle focus-visible:ring-2 focus-visible:ring-ring/40 disabled:text-muted-foreground"
          >
            {t("iam.templates.dialog.pickerAddOrRemove", "Add or remove")}
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          </button>
        </PopoverTrigger>
      </div>
    </PopoverAnchor>
  );
}

function OrgOptionRow({
  id,
  mode,
  org,
  isSelected,
  isActive,
  t,
  onToggle,
  onHover,
}: {
  id: string;
  mode: "single" | "multi";
  org: OrgPickerItem;
  isSelected: boolean;
  isActive: boolean;
  t: TFunction;
  onToggle: () => void;
  onHover?: () => void;
}) {
  if (mode === "single") {
    return (
      <button
        id={id}
        type="button"
        role="option"
        tabIndex={-1}
        aria-selected={isSelected}
        aria-label={org.name}
        onClick={onToggle}
        onMouseMove={onHover}
        className={cn(
          "flex min-h-9 w-full select-none items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-xs outline-none",
          isSelected && "bg-primary-subtle",
          isActive && "ring-2 ring-inset ring-ring/40",
          !isSelected && isActive && "bg-accent",
        )}
      >
        <OrgMark org={org} size={LIST_LOGO_SIZE} />
        <span className={cn("min-w-0 flex-1 truncate text-[13px] text-foreground", isSelected && "font-medium")}>
          {org.name}
        </span>
        <OrgTypeTag type={org.type} t={t} />
        <Check className={cn("h-4 w-4 shrink-0 text-primary", !isSelected && "invisible")} aria-hidden />
      </button>
    );
  }
  return (
    <label className="flex min-h-9 cursor-pointer select-none items-center gap-2.5 rounded-md px-2 py-1.5 text-xs hover:bg-accent">
      <Checkbox checked={isSelected} onCheckedChange={onToggle} aria-label={org.name} className="h-3.5 w-3.5" />
      <OrgMark org={org} size={LIST_LOGO_SIZE} />
      <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">{org.name}</span>
      <OrgTypeTag type={org.type} t={t} />
    </label>
  );
}

function OptionGroupHeading({ children }: { children: ReactNode }) {
  return (
    <p className="px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-label text-muted-foreground">{children}</p>
  );
}

function OrgMark({ org, size }: { org: OrgPickerItem; size: number }) {
  return (
    <OrgLogoMark
      name={org.name}
      logoUrl={org.logoUrl}
      accent={org.brandColor}
      isSuperTenant={org.type === "SUPER_TENANT"}
      size={size}
    />
  );
}

function OrgTypeTag({ type, t }: { type?: string; t: TFunction }) {
  if (type === "SUPER_TENANT") {
    return (
      <span className="shrink-0 rounded bg-primary-subtle px-1.5 py-0.5 text-[10px] font-medium text-primary-subtle-foreground">
        {t("iam.templates.dialog.orgTypePlatform", "Platform")}
      </span>
    );
  }
  if (type === "API") {
    return (
      <span className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
        {t("iam.templates.dialog.orgTypeApi", "API")}
      </span>
    );
  }
  return null;
}

/** Where Tab would have landed had focus not been inside the portaled list. */
function focusNextTabbable(from: Element | null): void {
  if (!(from instanceof HTMLElement)) return;
  const scope = from.closest('[role="dialog"]') ?? document.body;
  const tabbables = [...scope.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)].filter((el) => el.tabIndex >= 0);
  tabbables[tabbables.indexOf(from) + 1]?.focus();
}
